import { Component, computed, inject } from '@angular/core';
import { GameViewportService } from '@app/core/services/game-viewport.service';

const BUTTON_CLASS =
  'flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-base leading-none text-slate-300 hover:bg-slate-800 hover:text-slate-100 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent';

@Component({
  selector: 'app-zoom-controls',
  template: `
    <div
      class="pointer-events-auto absolute right-3 bottom-3 z-30 flex items-center gap-0.5 rounded-lg border border-slate-700 bg-slate-900/90 p-1 shadow-lg backdrop-blur-sm"
      role="group"
      aria-label="Map zoom"
    >
      <button
        type="button"
        [class]="BUTTON_CLASS"
        aria-label="Zoom out"
        title="Zoom out (-)"
        [disabled]="!viewport.canZoomOut()"
        (click)="viewport.zoomOut()"
      >
        &minus;
      </button>
      <button
        type="button"
        class="cursor-pointer rounded-md px-2 py-1 font-mono text-xs text-slate-300 tabular-nums hover:bg-slate-800 hover:text-slate-100 disabled:cursor-default disabled:hover:bg-transparent"
        aria-label="Fit the whole grid in view"
        title="Fit the whole grid (0) — scroll to zoom, hold Space to drag the map"
        [disabled]="viewport.isFitted()"
        (click)="viewport.resetView()"
      >
        {{ zoomPercent() }}%
      </button>
      <button
        type="button"
        [class]="BUTTON_CLASS"
        aria-label="Zoom in"
        title="Zoom in (+)"
        [disabled]="!viewport.canZoomIn()"
        (click)="viewport.zoomIn()"
      >
        +
      </button>
    </div>
  `,
  host: {
    '(document:keydown)': 'onKeydown($event)',
  },
})
export class ZoomControlsComponent {
  protected readonly viewport = inject(GameViewportService);
  protected readonly BUTTON_CLASS = BUTTON_CLASS;
  protected readonly zoomPercent = computed(() => Math.round(this.viewport.zoom() * 100));

  protected onKeydown(event: KeyboardEvent): void {
    if (event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;

    switch (event.key) {
      case '+':
      case '=':
        this.viewport.zoomIn();
        break;
      case '-':
        this.viewport.zoomOut();
        break;
      case '0':
        this.viewport.resetView();
        break;
      default:
        return;
    }
    event.preventDefault();
  }
}
